import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { userAPI } from '../services/api';
import { 
  CalendarIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ChartBarIcon,
  CurrencyDollarIcon 
} from '@heroicons/react/24/outline';

const History = () => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [dateRange, setDateRange] = useState({
    start: '',
    end: '',
  });
  const [isFiltering, setIsFiltering] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    fetchHistory();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getHistory();
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const filters = useMemo(() => [
    { id: 'all', label: 'All' },
    { id: 'deposits', label: 'Deposits' },
    { id: 'withdrawals', label: 'Withdrawals' },
    { id: 'earnings', label: 'Earnings' },
  ], []);

  // Memoize filtered transactions to avoid recalculating on every render
  const filteredTransactions = useMemo(() => {
    if (!history) return [];

    let transactions = [];

    if (activeFilter === 'all' || activeFilter === 'deposits') {
      transactions = transactions.concat(
        history.deposits?.map(deposit => ({
          ...deposit,
          type: 'deposit',
          icon: ArrowDownTrayIcon,
          color: 'cyan',
        })) || []
      );
    }

    if (activeFilter === 'all' || activeFilter === 'withdrawals') {
      transactions = transactions.concat(
        history.withdrawals?.map(withdrawal => ({
          ...withdrawal,
          type: 'withdrawal',
          icon: ArrowUpTrayIcon,
          color: 'red',
        })) || []
      );
    }

    if (activeFilter === 'all' || activeFilter === 'earnings') {
      transactions = transactions.concat(
        history.earnings?.map(earning => ({
          ...earning,
          type: earning.type === 'hourly' ? 'hourly_earning' : 'plan_purchase',
          icon: earning.type === 'hourly' ? CurrencyDollarIcon : ChartBarIcon,
          color: earning.type === 'hourly' ? 'blue' : 'purple',
        })) || []
      );
    }

    // Apply date filter if both dates are set
    let filtered = transactions;
    if (dateRange.start && dateRange.end) {
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999); // Include the entire end day
      
      filtered = transactions.filter(t => {
        const txDate = new Date(t.date);
        return txDate >= startDate && txDate <= endDate;
      });
    }

    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [history, activeFilter, dateRange.start, dateRange.end]);

  // Memoize summary stats
  const { totalDeposited, totalWithdrawn } = useMemo(() => {
    const deposited = history?.deposits
      ?.filter(d => d.status === 'approved')
      .reduce((sum, d) => sum + (d.amount || 0), 0) || 0;
    const withdrawn = history?.withdrawals
      ?.filter(w => w.status === 'approved')
      .reduce((sum, w) => sum + (w.amount || 0), 0) || 0;
    return { totalDeposited: deposited, totalWithdrawn: withdrawn };
  }, [history]);

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'approved': return '#00d4ff';
      case 'pending': return '#f59e0b';
      case 'rejected': return '#ef4444';
      default: return '#6b7280';
    }
  }, []);

  const getStatusBgColor = useCallback((status) => {
    switch (status) {
      case 'approved': return 'rgba(0, 212, 255, 0.1)';
      case 'pending': return 'rgba(245, 158, 11, 0.1)';
      case 'rejected': return 'rgba(239, 68, 68, 0.1)';
      default: return 'rgba(107, 114, 128, 0.1)';
    }
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const formatAmount = useCallback((type, amount) => {
    const prefix = type === 'deposit' || type.includes('earning') ? '+' : '-';
    const color = type === 'deposit' || type.includes('earning') ? '#00d4ff' : '#ef4444';
    return { text: `${prefix}$${Number(amount).toFixed(2)}`, color };
  }, []);

  const handleDateFilter = useCallback(() => {
    // Just trigger re-render - filteredTransactions will update via useMemo
    setIsFiltering(true);
    setTimeout(() => setIsFiltering(false), 300);
  }, []);

  const handleClearDateFilter = useCallback(() => {
    setDateRange({ start: '', end: '' });
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        backgroundColor: '#0a0a0a'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(0, 212, 255, 0.2)',
          borderTop: '4px solid #00d4ff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
      paddingBottom: isMobile ? '80px' : '20px',
      backgroundColor: '#0a0a0a',
      minHeight: '100vh'
    }}>
      {/* Header - Dark Theme */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
        borderRadius: '20px',
        padding: '24px',
        color: 'white',
        marginBottom: '24px',
        boxShadow: '0 10px 30px rgba(0, 212, 255, 0.2)',
        border: '1px solid rgba(0, 212, 255, 0.3)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 0 8px 0',
              background: 'linear-gradient(135deg, #fff, #00d4ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>Transaction History</h1>
            <p style={{
              opacity: '0.7',
              margin: 0,
              fontSize: '14px'
            }}>Track all your financial activities</p>
          </div>
          <CalendarIcon style={{ width: '32px', height: '32px', color: '#00d4ff' }} />
        </div>
      </div>

      {/* Filters - Dark Theme */}
      <div style={{
        background: '#1a1a2e',
        borderRadius: '20px',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          gap: '8px',
          background: '#0f0f0f',
          padding: '4px',
          borderRadius: '12px',
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              style={{
                flex: 1,
                padding: '12px',
                background: activeFilter === filter.id ? '#00d4ff' : 'transparent',
                border: 'none',
                borderRadius: '8px',
                color: activeFilter === filter.id ? '#1a1a2e' : '#94a3b8',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-end',
          flexWrap: 'wrap'
        }}>
          <div style={{ flex: 1 }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              color: '#94a3b8',
              marginBottom: '4px'
            }}>From</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
              style={{
                width: '100%',
                padding: '8px 12px',
                background: '#0f0f0f',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px'
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              color: '#94a3b8',
              marginBottom: '4px'
            }}>To</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
              style={{
                width: '100%',
                padding: '8px 12px',
                background: '#0f0f0f',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {(dateRange.start || dateRange.end) && (
              <button 
                onClick={handleClearDateFilter}
                style={{
                  padding: '8px 16px',
                  background: '#0f0f0f',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  borderRadius: '8px',
                  color: '#94a3b8',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Clear
              </button>
            )}
            <button 
              onClick={handleDateFilter}
              disabled={!dateRange.start || !dateRange.end}
              style={{
                padding: '8px 20px',
                background: (!dateRange.start || !dateRange.end) ? '#334155' : '#00d4ff',
                border: 'none',
                borderRadius: '8px',
                color: (!dateRange.start || !dateRange.end) ? '#94a3b8' : '#1a1a2e',
                fontSize: '14px',
                fontWeight: '500',
                cursor: (!dateRange.start || !dateRange.end) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                opacity: (!dateRange.start || !dateRange.end) ? 0.5 : 1
              }}
            >
              <FunnelIcon style={{ width: '16px', height: '16px' }} />
              Filter
            </button>
          </div>
        </div>
        {isFiltering && (
          <div style={{
            marginTop: '12px',
            fontSize: '12px',
            color: '#00d4ff',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              border: '2px solid #00d4ff',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
            Filtering transactions...
          </div>
        )}
      </div>

      {/* Summary Stats - Dark Theme */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          background: '#1a1a2e',
          borderRadius: '16px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: 'rgba(0, 212, 255, 0.1)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <ArrowDownTrayIcon style={{ width: '24px', height: '24px', color: '#00d4ff' }} />
          </div>
          <div>
            <p style={{
              fontSize: '12px',
              color: '#94a3b8',
              marginBottom: '4px'
            }}>Total Deposited</p>
            <p style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#00d4ff',
              margin: 0
            }}>${totalDeposited.toFixed(2)}</p>
          </div>
        </div>
        
        <div style={{
          background: '#1a1a2e',
          borderRadius: '16px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <ArrowUpTrayIcon style={{ width: '24px', height: '24px', color: '#ef4444' }} />
          </div>
          <div>
            <p style={{
              fontSize: '12px',
              color: '#94a3b8',
              marginBottom: '4px'
            }}>Total Withdrawn</p>
            <p style={{
              fontSize: '24px',
              fontWeight: '700',
              color: 'white',
              margin: 0
            }}>${totalWithdrawn.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Transactions List - Dark Theme */}
      <div style={{
        background: '#1a1a2e',
        borderRadius: '24px',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#00d4ff',
            margin: 0
          }}>Recent Transactions</h2>
          <span style={{
            fontSize: '14px',
            color: '#94a3b8'
          }}>{filteredTransactions.length} transactions</span>
        </div>

        {filteredTransactions.length > 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {filteredTransactions.slice(0, 50).map((transaction, index) => {
              const Icon = transaction.icon;
              const typeLabels = {
                deposit: 'Deposit',
                withdrawal: 'Withdrawal',
                hourly_earning: 'Hourly Earning',
                plan_purchase: 'Plan Purchase',
              };
              const amountData = formatAmount(transaction.type, transaction.amount);
              
              return (
                <div key={transaction.id || index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px',
                  background: '#0f0f0f',
                  borderRadius: '12px',
                  transition: 'all 0.2s',
                  flexWrap: 'wrap',
                  gap: '12px',
                  border: '1px solid rgba(0, 212, 255, 0.1)'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: `rgba(${transaction.color === 'cyan' ? '0, 212, 255' : transaction.color === 'red' ? '239, 68, 68' : transaction.color === 'blue' ? '59, 130, 246' : '139, 92, 246'}, 0.1)`,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Icon style={{
                      width: '24px',
                      height: '24px',
                      color: transaction.color === 'cyan' ? '#00d4ff' : transaction.color === 'red' ? '#ef4444' : transaction.color === 'blue' ? '#3b82f6' : '#8b5cf6'
                    }} />
                  </div>
                  
                  <div style={{ flex: 1, minWidth: '150px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      flexWrap: 'wrap',
                      marginBottom: '8px'
                    }}>
                      <h3 style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: 'white',
                        margin: 0
                      }}>
                        {typeLabels[transaction.type] || transaction.type}
                      </h3>
                      <span style={{
                        background: getStatusBgColor(transaction.status),
                        color: getStatusColor(transaction.status),
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {transaction.status}
                      </span>
                    </div>
                    <p style={{
                      fontSize: '12px',
                      color: '#94a3b8',
                      margin: '0 0 4px 0'
                    }}>
                      {formatDate(transaction.date)}
                    </p>
                    {transaction.type === 'deposit' && transaction.method && (
                      <p style={{
                        fontSize: '11px',
                        color: '#64748b',
                        margin: 0
                      }}>
                        Method: {transaction.method}
                      </p>
                    )}
                    {transaction.rejectReason && (
                      <p style={{
                        fontSize: '11px',
                        color: '#ef4444',
                        margin: '4px 0 0 0'
                      }}>
                        Reason: {transaction.rejectReason}
                      </p>
                    )}
                  </div>

                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: amountData.color,
                    whiteSpace: 'nowrap'
                  }}>
                    {amountData.text}
                  </div>
                </div>
              );
            })}
            {filteredTransactions.length > 50 && (
              <div style={{
                textAlign: 'center',
                padding: '20px',
                color: '#94a3b8',
                fontSize: '13px'
              }}>
                Showing 50 of {filteredTransactions.length} transactions
              </div>
            )}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px'
          }}>
            <CalendarIcon style={{
              width: '48px',
              height: '48px',
              color: '#64748b',
              margin: '0 auto 16px'
            }} />
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'white',
              marginBottom: '8px'
            }}>No transactions found</h3>
            <p style={{
              color: '#94a3b8',
              marginBottom: '20px'
            }}>
              {activeFilter === 'all' 
                ? "You don't have any transactions yet"
                : `No ${activeFilter} found for the selected period`
              }
            </p>
            {activeFilter !== 'all' && (
              <button 
                onClick={() => setActiveFilter('all')}
                style={{
                  padding: '10px 24px',
                  background: '#00d4ff',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#1a1a2e',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                View All Transactions
              </button>
            )}
          </div>
        )}
      </div>

      {/* Export Option - Dark Theme */}
      <div>
        <button 
          onClick={() => {
            alert('Export feature coming soon!');
          }}
          style={{
            width: '100%',
            padding: '16px',
            background: '#1a1a2e',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            borderRadius: '16px',
            color: '#00d4ff',
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.border = '1px solid #00d4ff';
            e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.2)';
            e.currentTarget.style.background = '#1a1a2e';
          }}
        >
          <ArrowDownTrayIcon style={{ width: '20px', height: '20px' }} />
          Export History (CSV)
        </button>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 640px) {
          .transaction-item {
            flex-direction: column;
            align-items: flex-start !important;
          }
        }
        body {
          background-color: #0a0a0a;
        }
      `}</style>
    </div>
  );
};

export default History;